import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Contact() {
  useEffect(() => { document.title = 'Contact SpinWorld.Us'; }, []);
  const [submitted, setSubmitted] = useState(false);

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Hero */}
      <section className="bg-[#2c2a4a] py-14 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Get in Touch</h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Have a question about our wind spinners, soapstone animals, or wire-wrapped stones? We would love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-5">Contact Information</h2>
                <div className="space-y-5">
                    <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#d1bedd] rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-lg text-[#2c2a4a]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Address</p>
                      <p className="text-sm text-[#4A4A4A] font-sans">5316 Trafalgar Place<br />Madison, WI 53714</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#d1bedd] rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-lg text-[#2c2a4a]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Phone</p>
                      <a href="tel:907-500-8881" className="text-sm text-[#4f528d] hover:underline font-sans">
                        907-500-8881
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#d1bedd] rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-lg text-[#2c2a4a]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Email</p>
                      <a href="mailto:Spinworld@myyahoo.com" className="text-sm text-[#4f528d] hover:underline font-sans">
                        Spinworld@myyahoo.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#d1bedd] rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-lg text-[#2c2a4a]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Response Time</p>
                      <p className="text-sm text-[#4A4A4A] font-sans">We respond within 1-2 business days.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A] mb-3">Follow Us</p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/alaskansartnaround/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#2c2a4a] rounded-full flex items-center justify-center text-white hover:bg-[#4f528d] transition-colors"
                  >
                    <i className="ri-facebook-fill text-lg" />
                  </a>
                  <a
                    href="https://www.yelp.com/biz/alaskans-art-n-around-juneau"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#2c2a4a] rounded-full flex items-center justify-center text-white hover:bg-[#4f528d] transition-colors"
                  >
                    <span className="text-sm font-bold">Y</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-6 md:p-8">
                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-[#d1bedd] rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-double-line text-2xl text-[#88d6f8]" />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#1A1A1A] mb-2">Message Sent!</h3>
                    <p className="text-[#8B7355] mb-6">Thank you for reaching out. We will get back to you within 1-2 business days.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-sm text-[#4f528d] hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d86sg1ao8uvplcbvqg90"
                    method="POST"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formEl = e.currentTarget;
                      const data = new URLSearchParams();
                      const inputs = formEl.querySelectorAll('input, textarea, select');
                      inputs.forEach((input) => {
                        const el = input as HTMLInputElement;
                        if (el.name) data.append(el.name, el.value);
                      });
                      fetch(formEl.action, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: data.toString(),
                      }).then(() => setSubmitted(true));
                    }}
                  >
                    <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-5">Send Us a Message</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        placeholder="you@example.com"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Subject</label>
                      <select
                        name="subject"
                        required
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B87333] cursor-pointer"
                      >
                        <option value="">Select a topic...</option>
                        <option value="product-question">Product Question</option>
                        <option value="order-status">Order Status</option>
                        <option value="shipping">Shipping Question</option>
                        <option value="returns">Returns & Refunds</option>
                        <option value="wholesale">Wholesale Inquiry</option>
                        <option value="other">Something Else</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Message</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333] resize-none"
                        placeholder="How can we help you?"
                      />
                      <p className="text-xs text-[#8B7355] mt-1">Maximum 500 characters.</p>
                    </div>

                    <button
                      type="submit"
                      className="w-full sm:w-auto py-3.5 px-10 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors whitespace-nowrap"
                    >
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}